// AvisoLegalPage.tsx - Página de Aviso Legal para Algorithmia

import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ScaleIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import LogoAlgorithmia from '../../src/assets/LOGOAZUL-Ajpg.jpg';

const AvisoLegalPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Aviso Legal | Algorithmia - Desarrollo de Software en Córdoba"
        description="Aviso legal y términos de uso de Algorithmia. Información sobre propiedad intelectual, responsabilidades y condiciones de uso del sitio web."
        keywords="aviso legal, términos de uso, propiedad intelectual, condiciones sitio web, algorithmia legal"
        canonicalUrl="https://algorithmia.com.ar/aviso-legal"  // ✅ corregido: apunta a sí misma
        url="https://algorithmia.com.ar/aviso-legal"           // ✅ corregido: igual que canonical
        noIndex={true}                                          // ✅ agregado: no indexar páginas legales
      />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        {/* Header */}
        <nav className="sticky top-0 z-40 py-3 bg-white/95 backdrop-blur-lg border-b border-gray-200 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <motion.button
              whileHover={{ scale: 1.05, x: -5 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-gray-600 font-medium text-sm hover:bg-gray-100 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Volver al Inicio</span>
            </motion.button>
            
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-lg flex items-center justify-center shadow-lg overflow-hidden">
                <img src={LogoAlgorithmia} alt="Algorithmia Logo" className="h-8 w-auto p-0.5" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-[#4ADE80] via-[#3BC571] to-[#2D4A7D] bg-clip-text text-transparent">
                Algorithmia
              </span>
            </div>
          </div>
        </nav>

        {/* Contenido */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100"
          >
            {/* Icono y título */}
            <div className="flex items-center gap-4 mb-8">
              <div className="p-4 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-2xl shadow-lg">
                <ScaleIcon className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Aviso Legal</h1>
            </div>

            {/* Fecha de actualización */}
            <p className="text-sm text-gray-500 mb-8 pb-4 border-b border-gray-100">
              Última actualización: {new Date().toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            {/* Secciones de contenido */}
            <div className="space-y-8">
              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">1. Datos Identificativos</h2>
                <p className="text-gray-700 mb-2">
                  <strong>Denominación social:</strong> Algorithmia<br />
                  <strong>Nombre comercial:</strong> Algorithmia<br />
                  <strong>Domicilio social:</strong> Córdoba, Argentina<br />
                  <strong>Correo electrónico:</strong> <a href="mailto:contacto@algorithmia.com.ar" className="text-[#4ADE80] hover:underline">contacto@algorithmia.com.ar</a><br />
                  <strong>Teléfono:</strong> +54 (351) 2489015 / +54 (351) 2021393
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">2. Propiedad Intelectual e Industrial</h2>
                <p className="text-gray-700 leading-relaxed">
                  Todos los derechos de propiedad intelectual e industrial del sitio web 
                  algorithmia.com.ar, su diseño, logotipos, textos, imágenes y código fuente 
                  pertenecen a Algorithmia. Queda prohibida la reproducción, distribución 
                  o modificación sin autorización expresa.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">3. Exclusión de Responsabilidad</h2>
                <p className="text-gray-700 leading-relaxed">
                  Algorithmia no se hace responsable de los daños o perjuicios derivados 
                  del uso de la información contenida en este sitio web, ni de los posibles 
                  errores de seguridad que se puedan producir por el uso de navegadores no 
                  actualizados.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">4. Ley Aplicable y Jurisdicción</h2>
                <p className="text-gray-700 leading-relaxed">
                  Las presentes condiciones se rigen por la legislación argentina. Para 
                  cualquier controversia que pudiera derivarse del acceso o uso del sitio 
                  web, las partes se someten a los juzgados y tribunales de la ciudad de 
                  Córdoba, Argentina.
                </p>
              </section>
            </div>

            {/* Contacto adicional */}
            <div className="mt-12 p-6 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-gray-700 text-center">
                ¿Tienes dudas sobre nuestro aviso legal? Contáctanos en{' '}
                <a href="mailto:contacto@algorithmia.com.ar" className="text-[#4ADE80] font-semibold hover:underline">
                  contacto@algorithmia.com.ar
                </a>
              </p>
            </div>
          </motion.div>
        </div>

        {/* Footer simple */}
        <footer className="py-8 bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-gray-600 text-sm">
              © {new Date().getFullYear()} Algorithmia. Todos los derechos reservados.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AvisoLegalPage;
